import { Box, Popover, Stack, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Button, HSpacer, IconButton, Input, Text } from '@/components/DesignSystem';
import './DatePickerStyles.css';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DatePickerCalendar } from './DatePickerCalendar';
import { useTranslation } from 'react-i18next';

export interface DateTimePickerDropdownProps {
  anchorEl?: HTMLElement | null,
  disabled?: boolean,
  errorMessage?: string,
  hideHelperText?: boolean,
  label: string,
  onChange: (value?: Date) => void,
  onClose?: () => void,
  testID: string,
  value?: Date,
  width?: number,
}

export const DateTimePickerDropdown = ({
  anchorEl: externalAnchorEl,
  disabled,
  errorMessage,
  hideHelperText = false,
  label,
  onChange,
  onClose: externalOnClose,
  testID,
  value,
  width,
}: DateTimePickerDropdownProps) => {
  const [internalAnchorEl, setInternalAnchorEl] = useState<HTMLElement | null>(null);
  const anchorEl = externalAnchorEl !== undefined ? externalAnchorEl : internalAnchorEl;
  const [internalValue, setInternalValue] = useState(() => {
    if (value) {
      return new Date(value.getTime());
    }
    return new Date();
  });
  const [showFooter, setShowFooter] = useState(true);
  const [activeTab, setActiveTab] = useState<'date' | 'time'>('date');
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const { t } = useTranslation();

  // Time selection state
  const [selectedHour, setSelectedHour] = useState(
    internalValue ? internalValue.getHours() % 12 || 12 : 12,
  );
  const [selectedMinute, setSelectedMinute] = useState(
    internalValue ? internalValue.getMinutes() : 0,
  );
  const [selectedAmPm, setSelectedAmPm] = useState<'AM' | 'PM'>(
    internalValue ? (internalValue.getHours() >= 12 ? 'PM' : 'AM') : 'AM',
  );

  const handleClose = () => {
    if (externalAnchorEl === undefined) {
      setInternalAnchorEl(null);
    }
    if (externalOnClose) {
      externalOnClose();
    }
    setActiveTab('date'); // Reset to date tab when closing
  };

  const handleOk = () => {
    const updatedDate = new Date(internalValue.getTime());

    let hours = selectedHour;
    if (selectedAmPm === 'PM' && selectedHour < 12) {
      hours += 12;
    } else if (selectedAmPm === 'AM' && selectedHour === 12) {
      hours = 0;
    }

    updatedDate.setHours(hours, selectedMinute, 0, 0);

    onChange(updatedDate);
    handleClose();
  };

  useEffect(() => {
    if (value) {
      // Create a copy of the date to avoid reference issues
      setInternalValue(new Date(value.getTime()));

      // Update time state when value changes
      const hours = value.getHours();
      const displayHour = hours % 12 === 0 ? 12 : hours % 12;

      setSelectedHour(displayHour);
      setSelectedMinute(value.getMinutes());
      setSelectedAmPm(hours >= 12 ? 'PM' : 'AM');
    }
  }, [value]);

  const formattedValue = useMemo(() => {
    if (!value) {
      return "";
    }
    return DateTime.fromJSDate(value).toFormat("MM/dd/yyyy hh:mm a");
  }, [value]);

  const generateTimeOptions = () => {
    // Generate hours (1-12)
    const hours = Array.from({ length: 12 }, (_, i) => i + 1);

    // Generate minutes (00-55 in 5-minute increments)
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

    return { hours, minutes };
  };

  const { hours, minutes } = generateTimeOptions();



  return (
    <>
      <Input
        containerSx={{ borderBottom: '1px solid #D8D8D8' }}
        disabled={disabled}
        error={!!errorMessage}
        helperText={errorMessage
          ?? (!hideHelperText && t(($) => $.designSystem.datePicker.dateTimeFormatHint))}
        label={label}
        onClick={
          disabled || externalAnchorEl !== undefined
            ? undefined
            : (e: React.MouseEvent<HTMLDivElement>) => {
              setInternalAnchorEl(e.currentTarget);
            }
        }
        testID={`${testID}-input`}
        trailingIcon={disabled ? undefined : () => (
          <Box mr={0.8}>
            <IconButton testID={`${testID}-calendar-button`}>
              <CalendarTodayIcon sx={{ color: theme.palette.text.secondary }}/>
            </IconButton>
          </Box>
        )}
        value={formattedValue}
        variant="filled"
        width={width}
      />
      <Popover
        PaperProps={{
          sx: {
            borderRadius: "16px",
            width: "360px",
          },
        }}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={handleClose}
        open={open}
        sx={{
          marginTop: "7px",
        }}
      >
        {/* Tab selector */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            borderBottom: '1px solid #E0E0E0',
            padding: '8px 16px',
          }}
        >
          <Button
            color={activeTab === 'date' ? 'primary' : 'inherit'}
            onClick={() => setActiveTab('date')}
            startIcon={<CalendarTodayIcon />}
            sx={{ minWidth: '120px' }}
            testID={`${testID}-date-tab-button`}
            variant={activeTab === 'date' ? 'contained' : 'text'}
          >
            {t(($) => $.designSystem.datePicker.dateTab)}
          </Button>
          <Button
            color={activeTab === 'time' ? 'primary' : 'inherit'}
            onClick={() => setActiveTab('time')}
            startIcon={<AccessTimeIcon />}
            sx={{ minWidth: '120px' }}
            testID={`${testID}-time-tab-button`}
            variant={activeTab === 'time' ? 'contained' : 'text'}
          >
            {t(($) => $.designSystem.datePicker.timeTab)}
          </Button>
        </Stack>

        {/* Date picker content */}
        {activeTab === 'date' && (              <DatePickerCalendar
            internalStartDate={internalValue}
            onChange={(date) => {
              // Create a new date object to avoid reference issues and ensure correct date selection
              const newDate = new Date(date);

              // Fix for timezone issues: ensure the date is exactly as selected
              // by maintaining the year, month, and day explicitly
              newDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());

              // Preserve the time from the current internal value
              newDate.setHours(
                internalValue.getHours(),
                internalValue.getMinutes(),
                internalValue.getSeconds(),
                internalValue.getMilliseconds(),
              );
              setInternalValue(newDate);
            }}
            onShowMonthPicker={(show) => setShowFooter(!show)}
            onShowYearPicker={(show) => setShowFooter(!show)}
            testID={testID}
            variant="dropdown"
          />
        )}

        {/* Time picker content */}
        {activeTab === 'time' && (
          <Box sx={{ padding: '16px' }}>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              {/* Hours column */}
              <Stack
                sx={{
                  width: '33%',
                  height: '280px',
                  overflowY: 'auto',
                  padding: '8px',
                }}
              >
                <Text
                  category="body-small"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: 'center',
                    marginBottom: '8px',
                  }}
                >
                  {t(($) => $.designSystem.datePicker.hour)}
                </Text>
                {hours.map((hour) => (
                  <Box
                    data-testid={`${testID}-hour-${hour}`}
                    key={`hour-${hour}`}
                    onClick={() => setSelectedHour(hour)}
                    sx={{
                      padding: '8px 0',
                      textAlign: 'center',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      backgroundColor: selectedHour === hour
                        ? theme.palette.primary.main
                        : 'transparent',
                      color: selectedHour === hour
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: selectedHour === hour
                          ? theme.palette.primary.main
                          : theme.palette.action.hover,
                      },
                    }}
                  >
                    <Text category="body-medium">
                      {hour}
                    </Text>
                  </Box>
                ))}
              </Stack>

              {/* Minutes column */}
              <Stack
                sx={{
                  width: '33%',
                  height: '280px',
                  overflowY: 'auto',
                  padding: '8px',
                }}
              >
                <Text
                  category="body-small"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: 'center',
                    marginBottom: '8px',
                  }}
                >
                  {t(($) => $.designSystem.datePicker.minute)}
                </Text>
                {minutes.map((minute) => (
                  <Box
                    data-testid={`${testID}-minute-${minute}`}
                    key={`minute-${minute}`}
                    onClick={() => setSelectedMinute(minute)}
                    sx={{
                      padding: '8px 0',
                      textAlign: 'center',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      backgroundColor: selectedMinute === minute
                        ? theme.palette.primary.main
                        : 'transparent',
                      color: selectedMinute === minute
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: selectedMinute === minute
                          ? theme.palette.primary.main
                          : theme.palette.action.hover,
                      },
                    }}
                  >
                    <Text category="body-medium">
                      {minute.toString().padStart(2, '0')}
                    </Text>
                  </Box>
                ))}
              </Stack>

              {/* AM/PM column */}
              <Stack
                sx={{
                  width: '33%',
                  padding: '8px',
                  justifyContent: 'flex-start',
                }}
              >
                <Text
                  category="body-small"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: 'center',
                    marginBottom: '8px',
                  }}
                >
                  {t(($) => $.designSystem.datePicker.amPm)}
                </Text>
                <Box
                  data-testid={`${testID}-am-option`}
                  onClick={() => setSelectedAmPm('AM')}
                  sx={{
                    padding: '8px 0',
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    backgroundColor: selectedAmPm === 'AM'
                      ? theme.palette.primary.main
                      : 'transparent',
                    color: selectedAmPm === 'AM'
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor: selectedAmPm === 'AM'
                        ? theme.palette.primary.main
                        : theme.palette.action.hover,
                    },
                    marginBottom: '8px',
                  }}
                >
                  <Text category="body-medium">
                    {t(($) => $.designSystem.datePicker.am)}
                  </Text>
                </Box>
                <Box
                  data-testid={`${testID}-pm-option`}
                  onClick={() => setSelectedAmPm('PM')}
                  sx={{
                    padding: '8px 0',
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    backgroundColor: selectedAmPm === 'PM'
                      ? theme.palette.primary.main
                      : 'transparent',
                    color: selectedAmPm === 'PM'
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor: selectedAmPm === 'PM'
                        ? theme.palette.primary.main
                        : theme.palette.action.hover,
                    },
                  }}
                >
                  <Text category="body-medium">
                    {t(($) => $.designSystem.datePicker.pm)}
                  </Text>
                </Box>
              </Stack>
            </Stack>
          </Box>
        )}
        {showFooter && (
          <Stack
            alignItems="center"
            direction="row"
            height={52}
            justifyContent="flex-end"
            pb={1}
            px={1.5}
          >
            <Button
              onClick={handleClose}
              testID={`${testID}-cancel-button`}
              variant="text"
            >
              {t(($) => $.common.actions.cancel)}
            </Button>
            <HSpacer size="2" />
            <Button
              onClick={handleOk}
              testID={`${testID}-ok-button`}
              variant="text"
            >
              {t(($) => $.designSystem.datePicker.ok)}
            </Button>
          </Stack>
        )}
      </Popover>
    </>
  );
};
